/**
 * Cache de identidad por id para arrays que emiten las queries LIVE de
 * PowerSync (receta CC-23, empaquetada en meal `6b53c9a`; piloto CC-26 en
 * field-check).
 *
 * Las queries live emiten arrays y objetos NUEVOS en cada tick aunque el
 * contenido no cambie. Un `<For>` (no-keyed), un `<Show keyed>` o el
 * `renderItem` del Listbox de Kobalte reconcilian por identidad REFERENCIAL,
 * así que cada tick re-monta los nodos → los clicks caen en nodos stale
 * (CC-26: el Select respaldado por query "pierde el click") y los inputs
 * locales se resetean mid-sync.
 *
 * `reconcileById(prev, next)` devuelve un array que REUTILIZA el objeto
 * previo cuando su contenido no cambió (comparación superficial por defecto),
 * y el PROPIO array previo si nada cambió — así ni `<For>` ni `keyed` ni el
 * collection-memo de Kobalte re-montan nada en un tick sin cambios reales.
 *
 * Uso con Solid:
 * ```ts
 * const stableRows = createMemo<Row[]>((prev) =>
 *   reconcileById(prev ?? [], query.data ?? [], (r) => r.id),
 * );
 * ```
 */

function shallowEqual(a: object, b: object): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const key of aKeys) {
    if (!Object.is(Reflect.get(a, key), Reflect.get(b, key))) {
      return false;
    }
  }
  return true;
}

export function reconcileById<T extends object>(
  prev: T[],
  next: readonly T[],
  getId: (item: T) => string | number | null | undefined,
  isEqual: (a: T, b: T) => boolean = shallowEqual,
): T[] {
  const prevById = new Map<string | number, T>();
  for (const item of prev) {
    const id = getId(item);
    if (id !== null && id !== undefined) {
      prevById.set(id, item);
    }
  }

  let reusedAll = prev.length === next.length;
  const result = next.map((item, index) => {
    const id = getId(item);
    const previous = id === null || id === undefined ? undefined : prevById.get(id);
    if (previous && isEqual(previous, item)) {
      if (reusedAll && prev[index] !== previous) {
        reusedAll = false; // mismo contenido pero reordenado
      }
      return previous;
    }
    reusedAll = false;
    return item;
  });

  // Nada cambió (mismos objetos, mismo orden): conserva la identidad del ARRAY
  // para que los memos aguas abajo tampoco re-emitan.
  return reusedAll ? prev : result;
}
